import { CartItem } from '@domains/cart';
import { Product } from '@domains/product';

import { KopecksIso } from '@utils/isomorph';
import { isAnalyticsActive, pushAnalyticsEvent } from '@utils/metrika';

export const getYandexClientID = () => {
    if (!isAnalyticsActive()) return null;
    try {
        const match = document.cookie.match('(?:^|;)\\s*_ym_uid=([^;]*)');
        return match ? decodeURIComponent(match[1]) : null;
    } catch {
        return null;
    }
};

type EmptyPayload = Record<string, never>;
type IdPayload = Record<'id', number>;

type EcommerceCartProduct = {
    item_id?: string;
    item_name: string;
    price?: number;
    quantity?: number;
    item_brand?: string;
    item_variant?: string;
    item_category?: string;
};

type EcommerceProduct = Omit<EcommerceCartProduct, 'quantity'>;

type EcommerceEvent = {
    ecommerce: {
        items: EcommerceProduct[];
    };
};

type EcommercePurchaseOrderEvent = {
    ecommerce: {
        currency: 'RUB';
        transaction_id: string;
        shipping: number;
        value: number;
        items: EcommerceProduct[];
    };
};

type AddProductsEvent = { products: EcommerceCartProduct[] };

type DataLayerEvents = {
    view_item_list: EcommerceEvent;
    select_item: EcommerceEvent;
    view_item: EcommerceEvent;
    add_to_cart: EcommerceEvent;
    begin_checkout: EcommerceEvent;
    remove_from_cart: EcommerceEvent;
    purchase: EcommercePurchaseOrderEvent;
    request_sms_login: EmptyPayload;
    add_product_to_favorite: IdPayload;
    remove_product_from_favorite: IdPayload;
    add_recipe_to_favorite: IdPayload;
    remove_recipe_from_favorite: IdPayload;
    go_to_cart_from_header: EmptyPayload;
    go_to_checkout_from_cart: EmptyPayload;
    use_search: EmptyPayload;
    feedback_request: EmptyPayload;
    partner_request: EmptyPayload;
    subscribe_telegram_click: EmptyPayload;
    add_to_cart_from_recipe: AddProductsEvent;
};

type EventType = keyof DataLayerEvents;

/* No GTM callback or timeout: YTM consumes dataLayer synchronously and an
   analytics failure must never block an order, form or navigation. */
function sendDataLayerEvent<T extends EventType>(
    event: T,
    options: DataLayerEvents[T],
): Promise<void> {
    pushAnalyticsEvent({ event, ...options });
    return Promise.resolve();
}

function mapProduct(data: Product): EcommerceProduct {
    return {
        item_id: String(data.id),
        item_name: data.name,
        price: KopecksIso.to(data.measure_unit.price_in_kopecks.new),
    };
}

function mapCartProduct(data: CartItem): EcommerceCartProduct {
    return {
        item_id: String(data.product.id),
        item_name: data.product.name,
        price: KopecksIso.to(data.product.measure_unit.price_in_kopecks.new),
        quantity: data.quantity,
    };
}

export function sendShowProductsEvent(product: Product[]): void {
    void sendDataLayerEvent('view_item_list', {
        ecommerce: {
            items: product.map(mapProduct),
        },
    });
}

export function sendClickOnProductLinkEvent(product: Product): void {
    void sendDataLayerEvent('select_item', {
        ecommerce: {
            items: [mapProduct(product)],
        },
    });
}

export function sendShowProductDetailsEvent(product: Product): void {
    void sendDataLayerEvent('view_item', {
        ecommerce: {
            items: [mapProduct(product)],
        },
    });
}

export const sendViewCheckoutEvent = (products: CartItem[]) => {
    void sendDataLayerEvent('begin_checkout', {
        ecommerce: {
            items: products.map(mapCartProduct),
        },
    });
};

export function sendPurchaseOrderEvent({
    products,
    price,
    shipping,
    orderId,
}: {
    products: CartItem[];
    price: FeipApi.Kopecks;
    shipping: FeipApi.Kopecks;
    orderId: string;
}): void {
    void sendDataLayerEvent('purchase', {
        ecommerce: {
            currency: 'RUB',
            transaction_id: orderId,
            value: KopecksIso.to(price),
            shipping: KopecksIso.to(shipping),
            items: products.map(mapCartProduct),
        },
    });
}

// extracted from ZGRD project
export async function handleMarketingOnCartStateChange(
    currentProducts: CartItem[],
    previousProducts: CartItem[],
): Promise<void> {
    const addedToCart: CartItem[] = [];
    const removedFromCart: CartItem[] = [];

    currentProducts.forEach((currentItem) => {
        const previousItem = previousProducts.find(
            (item) => String(item.product.id) === String(currentItem.product.id),
        );
        if (previousItem !== undefined) {
            if (previousItem.quantity < currentItem.quantity) {
                addedToCart.push({
                    ...currentItem,
                    quantity: currentItem.quantity - previousItem.quantity,
                });
            } else if (previousItem.quantity > currentItem.quantity) {
                removedFromCart.push({
                    ...previousItem,
                    quantity: previousItem.quantity - currentItem.quantity,
                });
            }
        } else {
            addedToCart.push(currentItem);
        }
    });

    previousProducts.forEach((previousItem) => {
        const currentItem = currentProducts.find(
            (item) => String(item.product.id) === String(previousItem.product.id),
        );
        if (currentItem === undefined) {
            removedFromCart.push(previousItem);
        }
    });

    if (addedToCart.length > 0) {
        void sendDataLayerEvent('add_to_cart', {
            ecommerce: {
                items: addedToCart.map(mapCartProduct),
            },
        });
    }

    if (removedFromCart.length > 0) {
        void sendDataLayerEvent('remove_from_cart', {
            ecommerce: {
                items: removedFromCart.map(mapCartProduct),
            },
        });
    }
}

export const sendRequestSmsEvent = () => {
    void sendDataLayerEvent('request_sms_login', {});
};

export const sendAddProductToFavoriteEvent = (id: number) => {
    void sendDataLayerEvent('add_product_to_favorite', { id });
};

export const sendRemoveProductFromFavoriteEvent = (id: number) => {
    void sendDataLayerEvent('remove_product_from_favorite', { id });
};

export const sendAddRecipeToFavoriteEvent = (id: number) => {
    void sendDataLayerEvent('add_recipe_to_favorite', { id });
};

export const sendRemoveRecipeFromFavoriteEvent = (id: number) => {
    void sendDataLayerEvent('remove_recipe_from_favorite', { id });
};

export const sendGoToCartFromHeaderEvent = () => {
    void sendDataLayerEvent('go_to_cart_from_header', {});
};

export const sendGoToCheckoutFromCartEvent = () => {
    void sendDataLayerEvent('go_to_checkout_from_cart', {});
};

export const sendUseSearchEvent = () => {
    void sendDataLayerEvent('use_search', {});
};

export const sendFeedbackRequestEvent = () => {
    void sendDataLayerEvent('feedback_request', {});
};

export const sendPartnerRequestEvent = () => {
    void sendDataLayerEvent('partner_request', {});
};

export const sendSubscribeTelegramClickEvent = () => {
    void sendDataLayerEvent('subscribe_telegram_click', {});
};

export const sendAddToCartFromRecipeEvent = (products: Product[]) => {
    void sendDataLayerEvent('add_to_cart_from_recipe', {
        products: products.map(mapProduct),
    });
};
